/**
 * Unify Organization — extends FHIR R4 Organization.
 *
 * Represents therapy practices in Unify. The Organization resource stores
 * practice identity, branding, legal metadata, and ownership.
 *
 * ## Standard FHIR fields used by Unify
 *
 * | Field | Usage |
 * |-------|-------|
 * | `name` | Practice display name |
 * | `active` | Whether the practice is active |
 * | `identifier[]` | Seed tag, EIN (`https://unify.health/fhir/identifier/ein`), NPI (`http://hl7.org/fhir/sid/us-npi`), Salesforce ID |
 * |                  | Payer orgs: Sohar (`https://soharhealth.com/payer-id`), Stedi network (`https://www.stedi.com/healthcare/network`), etc. |
 * | `telecom[]` | Email (`system: 'email'`), phone (`system: 'phone'`), website (`system: 'url'`) |
 * | `address[]` | Legal / billing address |
 * | `type[]` | Organization type coding (e.g. `payer` for insurance payer Organizations) |
 *
 * ## Custom extensions
 *
 * | Extension URL | Value type | Purpose |
 * |---------------|------------|---------|
 * | `ADMIN_EXT_URLS.practiceOwner` | `valueReference` → Practitioner | The owning practitioner |
 * | `BRAND_PRIMARY_COLOR_EXT` | `valueString` | Hex colour for white-labelling |
 * | `BRAND_LOGO_URL_EXT` | `valueString` | URL to the practice logo |
 *
 * | `BRAND_SECONDARY_COLOR_EXT` | `valueString` | Secondary branding colour |
 * | `PRACTICE_BIO_EXT` | `valueString` | Practice bio / description |
 * | `PRACTICE_INTAKE_LINK_ENABLED_EXT` | `valueBoolean` | Intake availability toggle |
 * | `PRACTICE_DIRECTORY_EMBED_ENABLED_EXT` | `valueBoolean` | Directory embed toggle |
 * | `PRACTICE_PORTAL_TILES_EXT` | `valueString` (JSON) | Portal resource tiles |
| `PRACTICE_CANCELLATION_WINDOW_HOURS_EXT` | `valueInteger` | Cancellation window (hours) for email policy copy; editable in Practice Settings |
| `PRACTICE_EMAIL_RESPONSE_TIMEFRAME_EXT` | `valueString` | Email response timeframe for billing/statement emails; editable in Practice Settings |
 */
import type { Address, Organization, Reference, Extension } from '@medplum/fhirtypes';

import {
  ADMIN_EXT_URLS,
  BRAND_PRIMARY_COLOR_EXT,
  BRAND_LOGO_URL_EXT,
  BRAND_SECONDARY_COLOR_EXT,
  CHC_PAYER_ID_SYSTEM,
  EIN_IDENTIFIER_SYSTEM,
  NPI_IDENTIFIER_SYSTEM,
  ORG_KIND_BILLING_ENTITY,
  ORG_KIND_SYSTEM,
  ORG_TYPE_OTHER,
  ORG_TYPE_PAYER,
  ORG_TYPE_SYSTEM,
  PLATFORM_ORGANIZATION_IDENTIFIER_SYSTEM,
  PLATFORM_ORGANIZATION_IDENTIFIER_VALUE,
  STRIPE_CONNECTED_ACCOUNT_SYSTEM,
  US_EIN_IDENTIFIER_SYSTEM,
  PRACTICE_BIO_EXT,
  PRACTICE_INTAKE_LINK_ENABLED_EXT,
  PRACTICE_DIRECTORY_EMBED_ENABLED_EXT,
  PRACTICE_PORTAL_TILES_EXT,
  SOHAR_PAYER_ID_SYSTEM,
  STEDI_PAYER_ID_SYSTEM,
  STEDI_PAYER_ID_SYSTEMS,
  STEDI_PAYER_NETWORK_ID_SYSTEM,
  VERIFIABLE_PAYER_COMMERCIAL_ID_SYSTEM,
  VERIFIABLE_PAYER_MEDICAID_ID_SYSTEM,
  VERIFIABLE_PAYER_MEDICARE_ID_SYSTEM,
} from '../constants';

// ─── Private helpers ────────────────────────────────────────────────

function getExt(resource: Organization, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}

function setExt(resource: Organization, url: string, ext: Partial<Extension>): Organization {
  const extensions = (resource.extension ?? []).filter((e) => e.url !== url);
  extensions.push({ url, ...ext } as Extension);
  return { ...resource, extension: extensions };
}

// ─── Practice owner ─────────────────────────────────────────────────

export function getPracticeOwnerRef(resource: Organization): Reference | undefined {
  return getExt(resource, ADMIN_EXT_URLS.practiceOwner)?.valueReference;
}

export function setPracticeOwnerRef(resource: Organization, ref: Reference): Organization {
  return setExt(resource, ADMIN_EXT_URLS.practiceOwner, { valueReference: ref });
}

// ─── Brand: primary color ───────────────────────────────────────────

export function getBrandPrimaryColor(resource: Organization): string | undefined {
  return getExt(resource, BRAND_PRIMARY_COLOR_EXT)?.valueString;
}

export function setBrandPrimaryColor(resource: Organization, color: string): Organization {
  return setExt(resource, BRAND_PRIMARY_COLOR_EXT, { valueString: color });
}

// ─── Brand: logo URL ────────────────────────────────────────────────

export function getBrandLogoUrl(resource: Organization): string | undefined {
  return getExt(resource, BRAND_LOGO_URL_EXT)?.valueString;
}

export function setBrandLogoUrl(resource: Organization, url: string): Organization {
  return setExt(resource, BRAND_LOGO_URL_EXT, { valueString: url });
}

// ─── Brand: secondary color ─────────────────────────────────────────

export function getBrandSecondaryColor(resource: Organization): string | undefined {
  return getExt(resource, BRAND_SECONDARY_COLOR_EXT)?.valueString;
}

export function setBrandSecondaryColor(resource: Organization, color: string): Organization {
  return setExt(resource, BRAND_SECONDARY_COLOR_EXT, { valueString: color });
}

// ─── Practice bio ───────────────────────────────────────────────────

export function getPracticeBio(resource: Organization): string | undefined {
  return getExt(resource, PRACTICE_BIO_EXT)?.valueString;
}

export function setPracticeBio(resource: Organization, bio: string): Organization {
  return setExt(resource, PRACTICE_BIO_EXT, { valueString: bio });
}

// ─── Portal: intake link enabled ────────────────────────────────────

export function getIntakeLinkEnabled(resource: Organization): boolean | undefined {
  return getExt(resource, PRACTICE_INTAKE_LINK_ENABLED_EXT)?.valueBoolean;
}

export function setIntakeLinkEnabled(resource: Organization, enabled: boolean): Organization {
  return setExt(resource, PRACTICE_INTAKE_LINK_ENABLED_EXT, { valueBoolean: enabled });
}

// ─── Portal: directory embed enabled ────────────────────────────────

export function getDirectoryEmbedEnabled(resource: Organization): boolean | undefined {
  return getExt(resource, PRACTICE_DIRECTORY_EMBED_ENABLED_EXT)?.valueBoolean;
}

export function setDirectoryEmbedEnabled(resource: Organization, enabled: boolean): Organization {
  return setExt(resource, PRACTICE_DIRECTORY_EMBED_ENABLED_EXT, { valueBoolean: enabled });
}

// ─── Portal: tiles (JSON-encoded array) ─────────────────────────────

export interface PortalTileData {
  id: string;
  title: string;
  link: string;
  imageUrl?: string;
  order: number;
}

export function getPortalTiles(resource: Organization): PortalTileData[] | undefined {
  const raw = getExt(resource, PRACTICE_PORTAL_TILES_EXT)?.valueString;
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as PortalTileData[];
  } catch {
    return undefined;
  }
}

export function setPortalTiles(resource: Organization, tiles: PortalTileData[]): Organization {
  return setExt(resource, PRACTICE_PORTAL_TILES_EXT, { valueString: JSON.stringify(tiles) });
}

export function removeExt(resource: Organization, url: string): Organization {
  const extensions = (resource.extension ?? []).filter((e) => e.url !== url);
  return { ...resource, extension: extensions };
}

// ─── Payer organization identifiers ─────────────────────────────────

function getIdentifierValue(resource: Organization, system: string): string | undefined {
  const value = resource.identifier?.find((i) => i.system === system)?.value?.trim();
  return value || undefined;
}

function upsertIdentifier(
  resource: Organization,
  system: string,
  value: string | undefined,
): Organization {
  const identifiers = [...(resource.identifier ?? [])].filter((i) => i.system !== system);
  const trimmed = value?.trim();
  if (trimmed) {
    identifiers.push({ system, value: trimmed });
  }
  return { ...resource, identifier: identifiers.length ? identifiers : undefined };
}

/** True when `type` includes the accepted-payer directory code (`ORG_TYPE_PAYER`). */
export function isPayerOrganization(resource: Organization): boolean {
  return (
    resource.type?.some((t) =>
      t.coding?.some((c) => c.system === ORG_TYPE_SYSTEM && c.code === ORG_TYPE_PAYER),
    ) ?? false
  );
}

/** First Stedi-resolvable payer ID (network → payerId → CHC), matching `$stedi-submit-claim`. */
export function resolveStediPayerId(resource: Organization): string | undefined {
  for (const system of STEDI_PAYER_ID_SYSTEMS) {
    const value = getIdentifierValue(resource, system);
    if (value) return value;
  }
  return undefined;
}

export function getStediPayerNetworkId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, STEDI_PAYER_NETWORK_ID_SYSTEM);
}

export function setStediPayerNetworkId(
  resource: Organization,
  value: string | undefined,
): Organization {
  return upsertIdentifier(resource, STEDI_PAYER_NETWORK_ID_SYSTEM, value);
}

export function getStediPayerId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, STEDI_PAYER_ID_SYSTEM);
}

export function setStediPayerId(resource: Organization, value: string | undefined): Organization {
  return upsertIdentifier(resource, STEDI_PAYER_ID_SYSTEM, value);
}

export function getChcPayerId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, CHC_PAYER_ID_SYSTEM);
}

/** Legacy clearinghouse payer codes (e.g. Salesforce `TWVACCN`) for Stedi routing fallback. */
export function setChcPayerId(resource: Organization, value: string | undefined): Organization {
  return upsertIdentifier(resource, CHC_PAYER_ID_SYSTEM, value);
}

export function getSoharPayerId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, SOHAR_PAYER_ID_SYSTEM);
}

export function setSoharPayerId(resource: Organization, value: string | undefined): Organization {
  return upsertIdentifier(resource, SOHAR_PAYER_ID_SYSTEM, value);
}

export function getVerifiableMedicaidPayerId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, VERIFIABLE_PAYER_MEDICAID_ID_SYSTEM);
}

export function setVerifiableMedicaidPayerId(
  resource: Organization,
  value: string | undefined,
): Organization {
  return upsertIdentifier(resource, VERIFIABLE_PAYER_MEDICAID_ID_SYSTEM, value);
}

export function getVerifiableMedicarePayerId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, VERIFIABLE_PAYER_MEDICARE_ID_SYSTEM);
}

export function setVerifiableMedicarePayerId(
  resource: Organization,
  value: string | undefined,
): Organization {
  return upsertIdentifier(resource, VERIFIABLE_PAYER_MEDICARE_ID_SYSTEM, value);
}

export function getVerifiableCommercialPayerId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, VERIFIABLE_PAYER_COMMERCIAL_ID_SYSTEM);
}

export function setVerifiableCommercialPayerId(
  resource: Organization,
  value: string | undefined,
): Organization {
  return upsertIdentifier(resource, VERIFIABLE_PAYER_COMMERCIAL_ID_SYSTEM, value);
}

function addressHasNonStateContent(addr: Address): boolean {
  if (addr.line?.some((line) => line?.trim())) return true;
  if (addr.city?.trim()) return true;
  if (addr.district?.trim()) return true;
  if (addr.postalCode?.trim()) return true;
  if (addr.text?.trim()) return true;
  return false;
}

/** USPS 2-letter state on `address[0].state` (payer jurisdiction, not a full mailing address). */
export function getPayerState(resource: Organization): string | undefined {
  const state = resource.address?.[0]?.state?.trim();
  return state || undefined;
}

export function setPayerState(resource: Organization, value: string | undefined): Organization {
  const trimmed = value?.trim();
  const addresses = [...(resource.address ?? [])];
  const first: Address = addresses[0] ? { ...addresses[0] } : {};

  if (trimmed) {
    first.state = trimmed.toUpperCase();
    if (!first.country?.trim()) {
      first.country = 'US';
    }
    addresses[0] = first;
    return { ...resource, address: addresses };
  }

  if (!addresses[0]) {
    return resource;
  }

  delete first.state;
  if (!addressHasNonStateContent(first)) {
    addresses.shift();
  } else {
    addresses[0] = first;
  }
  return { ...resource, address: addresses.length ? addresses : undefined };
}

export interface BuildUnifyPlatformOrganizationParams {
  id?: string;
}

/** Builds the singleton Unify platform Organization (`type=other`, not a practice). */
export function buildUnifyPlatformOrganization(
  params: BuildUnifyPlatformOrganizationParams = {},
): Organization {
  return {
    resourceType: 'Organization',
    ...(params.id ? { id: params.id } : {}),
    name: 'Unify',
    active: true,
    identifier: [
      {
        system: PLATFORM_ORGANIZATION_IDENTIFIER_SYSTEM,
        value: PLATFORM_ORGANIZATION_IDENTIFIER_VALUE,
      },
    ],
    type: [
      {
        coding: [
          {
            system: ORG_TYPE_SYSTEM,
            code: ORG_TYPE_OTHER,
            display: 'Other',
          },
        ],
      },
    ],
  };
}

export interface BuildPayerOrganizationParams {
  name: string;
  soharId?: string;
  /** Stedi network payer ID (`https://www.stedi.com/healthcare/network`). */
  stediNetworkId?: string;
  /** Verifiable Medicaid payer ID (`https://unify.health/fhir/identifier/verifiable-payer-id-medicaid`). */
  verifiableMedicaidId?: string;
  /** Verifiable Medicare payer ID (`https://unify.health/fhir/identifier/verifiable-payer-id-medicare`). */
  verifiableMedicareId?: string;
  /** Verifiable Commercial payer ID (`https://unify.health/fhir/identifier/verifiable-payer-id-commercial`). */
  verifiableCommercialId?: string;
  /** USPS 2-letter state on `address[0].state`. */
  state?: string;
  /** Legacy clearinghouse payer code for CHC fallback routing. */
  chcPayerId?: string;
  active?: boolean;
}

/** Builds a payer-directory `Organization` with standard identifier systems (not custom `payer-id`). */
export function buildPayerOrganization(params: BuildPayerOrganizationParams): Organization {
  let org: Organization = {
    resourceType: 'Organization',
    name: params.name.trim(),
    active: params.active ?? true,
    type: [
      {
        coding: [
          {
            system: ORG_TYPE_SYSTEM,
            code: ORG_TYPE_PAYER,
            display: 'Insurance Company',
          },
        ],
      },
    ],
  };
  if (params.soharId?.trim()) {
    org = setSoharPayerId(org, params.soharId);
  }
  if (params.stediNetworkId?.trim()) {
    org = setStediPayerNetworkId(org, params.stediNetworkId);
  }
  if (params.verifiableMedicaidId?.trim()) {
    org = setVerifiableMedicaidPayerId(org, params.verifiableMedicaidId);
  }
  if (params.verifiableMedicareId?.trim()) {
    org = setVerifiableMedicarePayerId(org, params.verifiableMedicareId);
  }
  if (params.verifiableCommercialId?.trim()) {
    org = setVerifiableCommercialPayerId(org, params.verifiableCommercialId);
  }
  if (params.state?.trim()) {
    org = setPayerState(org, params.state);
  }
  if (params.chcPayerId?.trim()) {
    org = setChcPayerId(org, params.chcPayerId);
  }
  return org;
}

export const STRIPE_CONNECTED_ACCOUNT_ID_PATTERN = /^acct_[A-Za-z0-9]+$/;

export function isValidStripeConnectedAccountId(value: string): boolean {
  return STRIPE_CONNECTED_ACCOUNT_ID_PATTERN.test(value.trim());
}

export function billingEntityType(): Organization['type'] {
  return [
    {
      coding: [
        {
          system: ORG_KIND_SYSTEM,
          code: ORG_KIND_BILLING_ENTITY,
          display: 'Billing entity',
        },
      ],
    },
  ];
}

export function isBillingEntityOrganization(resource: Organization): boolean {
  return (
    resource.type?.some((t) =>
      t.coding?.some((c) => c.system === ORG_KIND_SYSTEM && c.code === ORG_KIND_BILLING_ENTITY),
    ) ?? false
  );
}

export function ensureBillingEntityType(resource: Organization): Organization {
  return { ...resource, type: billingEntityType() };
}

export function getOrganizationNpi(resource: Organization): string | undefined {
  return getIdentifierValue(resource, NPI_IDENTIFIER_SYSTEM);
}

export function setOrganizationNpi(resource: Organization, value: string | undefined): Organization {
  return upsertIdentifier(resource, NPI_IDENTIFIER_SYSTEM, value);
}

export function getOrganizationEin(resource: Organization): string | undefined {
  return (
    getIdentifierValue(resource, EIN_IDENTIFIER_SYSTEM) ??
    getIdentifierValue(resource, US_EIN_IDENTIFIER_SYSTEM)
  );
}

export function setOrganizationEin(resource: Organization, value: string | undefined): Organization {
  return upsertIdentifier(
    upsertIdentifier(resource, EIN_IDENTIFIER_SYSTEM, value),
    US_EIN_IDENTIFIER_SYSTEM,
    value,
  );
}

export function getStripeConnectedAccountId(resource: Organization): string | undefined {
  return getIdentifierValue(resource, STRIPE_CONNECTED_ACCOUNT_SYSTEM);
}

export function setStripeConnectedAccountId(
  resource: Organization,
  value: string | undefined,
): Organization {
  const trimmed = value?.trim();
  if (trimmed && !isValidStripeConnectedAccountId(trimmed)) {
    throw new Error('Stripe connected-account id must match acct_…');
  }
  return upsertIdentifier(resource, STRIPE_CONNECTED_ACCOUNT_SYSTEM, trimmed);
}

export function getOrganizationPhone(resource: Organization): string | undefined {
  return resource.telecom?.find((t) => t.system === 'phone')?.value;
}

export function setOrganizationPhone(resource: Organization, value: string | undefined): Organization {
  const telecom = [...(resource.telecom ?? [])].filter((t) => t.system !== 'phone');
  const trimmed = value?.trim();
  if (trimmed) {
    telecom.push({ system: 'phone', value: trimmed, use: 'work' });
  }
  return { ...resource, telecom: telecom.length ? telecom : undefined };
}

export interface OrganizationUspsAddress {
  line?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export function getOrganizationUspsAddress(resource: Organization): OrganizationUspsAddress {
  const addr = resource.address?.[0];
  return {
    line: addr?.line?.[0],
    city: addr?.city,
    state: addr?.state,
    postalCode: addr?.postalCode,
  };
}

export function setOrganizationUspsAddress(
  resource: Organization,
  address: OrganizationUspsAddress,
): Organization {
  const line = address.line?.trim();
  const city = address.city?.trim();
  const state = address.state?.trim().toUpperCase();
  const postalCode = address.postalCode?.trim();
  if (!line && !city && !state && !postalCode) {
    const rest = (resource.address ?? []).slice(1);
    return { ...resource, address: rest.length ? rest : undefined };
  }
  const next: Address = { use: 'work', country: 'US' };
  if (line) next.line = [line];
  if (city) next.city = city;
  if (state) next.state = state;
  if (postalCode) next.postalCode = postalCode;
  return { ...resource, address: [next, ...(resource.address ?? []).slice(1)] };
}

export interface BuildBillingEntityOrganizationParams {
  name: string;
  active?: boolean;
  npi?: string;
  ein?: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  stripeConnectedAccountId?: string;
}

export function buildBillingEntityOrganization(
  params: BuildBillingEntityOrganizationParams,
): Organization {
  let org: Organization = {
    resourceType: 'Organization',
    name: params.name.trim(),
    active: params.active ?? true,
    type: billingEntityType(),
  };
  if (params.npi?.trim()) org = setOrganizationNpi(org, params.npi);
  if (params.ein?.trim()) org = setOrganizationEin(org, params.ein);
  if (params.phone?.trim()) org = setOrganizationPhone(org, params.phone);
  org = setOrganizationUspsAddress(org, {
    line: params.street,
    city: params.city,
    state: params.state,
    postalCode: params.postalCode,
  });
  if (params.stripeConnectedAccountId?.trim()) {
    org = setStripeConnectedAccountId(org, params.stripeConnectedAccountId);
  }
  return org;
}
